import i18next from 'i18next'
import type { Anchor, Course, CourseStatus, Module, QAEntry, OnboardingInput, Section, Checkpoint, CourseOutline, ModuleOutline, UserStats, ModuleNote, ModuleTransform } from '../types'

// Client-authored failure strings route through the shared i18next
// module (same pattern as lib/activity.ts) rather than useTranslation —
// this file isn't a component. i18next.t reads i18n.language at call
// time, so toasts come out in the active UI locale (audit B7). Keys
// live in common.json's `errors` section across all shipped locales.
type ClientErrorKey =
  | 'timeout'
  | 'network'
  | 'session_expired'
  | 'rate_limited'
  | 'request_failed'
  | 'verify_email_gate'
const terr = (key: ClientErrorKey) => i18next.t(`common:errors.${key}`)

// Dev uses the Vite proxy (same-origin /api/v1). Production builds on Render
// set VITE_API_URL to the luno-api service's absolute URL because
// Render static-site rewrites silently drop POST bodies and can't be used
// to proxy the API.
const BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1'

// apiOrigin returns the origin (scheme + host + port, no path) of the
// API for cases where the API path needs to be prefixed onto a URL
// the frontend received from the backend (e.g. module_images.storage_url,
// which the backend writes as "/api/v1/lesson-images/<id>" — a relative
// path that resolves correctly in dev but lands on the wrong origin in
// prod where web + API are on separate services).
//
// Same-origin dev returns "" so the relative path resolves as-is.
// Cross-origin prod returns "https://luno-api.example.com" so the
// caller can construct "https://luno-api.example.com/api/v1/lesson-images/<id>".
export function apiOrigin(): string {
  if (/^https?:\/\//.test(BASE)) {
    try {
      return new URL(BASE).origin
    } catch {
      return ''
    }
  }
  return ''
}

// oauthStartURL is the URL the browser navigates to (top-level, not fetch) to
// begin a provider sign-in. The backend 302s on to the provider's consent
// screen and, on the callback, sets the session cookie before redirecting back
// to the SPA. Root-relative in dev (Vite proxies /api/v1 → backend),
// absolute in prod (VITE_API_URL points at the API origin).
export function oauthStartURL(provider: 'google'): string {
  return `${BASE}/auth/${provider}/start`
}

// #123 — session lives in an httpOnly Secure SameSite=Lax cookie set by
// the backend on /auth/login, /auth/register, /auth/refresh,
// /me/password, /auth/password-reset/confirm. The cookie is unreadable
// to this code, which is the whole point: a XSS bug that lands in the
// SPA can't exfiltrate the bearer.
//
// Every fetch needs credentials: 'include' so the cookie rides along on
// cross-origin requests (prod is luno-web.onrender.com → luno-api.…).
// The backend's chi/cors middleware echoes AllowCredentials: true with
// a finite AllowedOrigins allowlist, so this is safe — a third-party
// origin can't CORS-credential into us.
const CREDS: RequestCredentials = 'include'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public retryAfterSeconds?: number,
    // code is the backend's machine-readable error tag (currently only
    // "email_not_verified" populates it). UI code matches on this so the
    // verification gate can render its own affordance instead of a
    // generic toast.
    public code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// parseRetryAfter pulls a Retry-After header and normalises it to seconds.
// RFC 7231 allows either a non-negative integer (delta-seconds) or an HTTP-date.
// Returns undefined if the header is absent or unparseable.
export function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined
  const trimmed = header.trim()
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed)
  }
  const date = Date.parse(trimmed)
  if (Number.isNaN(date)) return undefined
  const seconds = Math.ceil((date - Date.now()) / 1000)
  return seconds > 0 ? seconds : 0
}

// Track whether a refresh is already in flight to avoid concurrent refreshes
let refreshPromise: Promise<boolean> | null = null

// tryRefresh is a no-arg POST. The session cookie rides along via
// credentials: 'include'; the backend rotates the cookie in the
// response. We don't read the body for anything load-bearing — the
// cookie swap is the contract. Returns false when refresh fails (the
// cookie is missing, the JWT is too old to refresh, or the server
// errored), letting the caller surface a session-expired flow.
async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: CREDS,
        headers: { 'Content-Type': 'application/json' },
      })
      return res.ok
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// Callbacks for auth events — set by useAuth
let onSessionExpired: (() => void) | null = null

export function setOnSessionExpired(cb: () => void) {
  onSessionExpired = cb
}

// Default per-request timeout. Without this, a fetch on a dropped /
// stalled connection hangs until the OS-level TCP timeout (minutes),
// which under Phase 4's optimistic flows leaves the UI claiming "saved"
// while the server hasn't actually heard a thing. 30s covers the slowest
// non-LLM endpoint we have (course list on a cold backend); LLM-bound
// endpoints override via the `timeoutMs` option below.
const DEFAULT_TIMEOUT_MS = 30_000

// LLM_TIMEOUT_MS mirrors the backend's http.Server.WriteTimeout for
// LLM-bound endpoints (askQuestion, …). The backend gives up at 120s,
// so a longer client-side timeout would have us waiting after the
// server has already disconnected. Pivot is given a little extra
// headroom because it generates a whole course branch.
export const LLM_TIMEOUT_MS = 120_000
export const PIVOT_TIMEOUT_MS = 180_000

export interface RequestOptions extends RequestInit {
  // Override the default per-request timeout. Pass `null` to disable
  // (rare — used by recordStudyHeartbeat which has its own keepalive
  // semantics and never gates UI on its result).
  timeoutMs?: number | null
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Wrap each fetch in an AbortController-driven timer so a stalled
  // connection surfaces as a typed ApiError instead of hanging forever.
  // Both the initial call and the post-401-refresh retry share the same
  // budget — a stalled refresh shouldn't extend the overall wait.
  const ctrl = timeoutMs == null ? null : new AbortController()
  const timer =
    ctrl != null && timeoutMs != null
      ? window.setTimeout(() => ctrl.abort(), timeoutMs)
      : null
  const signal = ctrl?.signal ?? fetchOptions.signal

  try {
    let res: Response
    try {
      res = await fetch(`${BASE}${path}`, {
        ...fetchOptions,
        credentials: CREDS,
        signal,
        headers: { ...headers, ...fetchOptions.headers as Record<string, string> },
      })
    } catch (err) {
      // AbortError surfaces as a DOMException; map it to ApiError so
      // callers can render a consistent "request timed out" toast.
      // Other errors (TypeError on network failure, …) get the same
      // treatment — at the UI layer they're indistinguishable.
      if ((err as DOMException | undefined)?.name === 'AbortError') {
        throw new ApiError(0, terr('timeout'))
      }
      throw new ApiError(0, terr('network'))
    }

    // If 401, try refreshing the cookie and retry once. /auth/* paths
    // skip the refresh loop — a 401 on login means bad credentials, not
    // an expired session. The retry sends the rotated cookie (the
    // backend issued a fresh Set-Cookie on the refresh response, which
    // the browser will replay automatically).
    if (res.status === 401 && !path.includes('/auth/')) {
      const refreshed = await tryRefresh()
      if (refreshed) {
        try {
          res = await fetch(`${BASE}${path}`, {
            ...fetchOptions,
            credentials: CREDS,
            signal,
            headers: { ...headers, ...fetchOptions.headers as Record<string, string> },
          })
        } catch (err) {
          if ((err as DOMException | undefined)?.name === 'AbortError') {
            throw new ApiError(0, terr('timeout'))
          }
          throw new ApiError(0, terr('network'))
        }
      } else {
        // Refresh failed — session is expired. The cookie has already
        // been cleared server-side by the middleware on any 401 with a
        // cookie present, so localStorage of credentials isn't
        // something we need to worry about here.
        if (onSessionExpired) onSessionExpired()
        throw new ApiError(401, terr('session_expired'))
      }
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: '' }))
      const retryAfter = res.status === 429 ? parseRetryAfter(res.headers.get('Retry-After')) : undefined
      const fallback = res.status === 429
        ? terr('rate_limited')
        : terr('request_failed')
      // body.error usually carries a clean handler-authored string
      // ("module not found", "quote is too long", …) but a leaky
      // generic-500 path can pass raw pgx / driver text through. Filter
      // those out so the user gets a generic message instead of
      // "pq: duplicate key value violates unique constraint…" or
      // "context deadline exceeded". Console-logged for debug.
      const raw = typeof body.error === 'string' ? body.error : ''
      const looksTechnical =
        !raw ||
        raw.length > 240 ||
        /^(pq:|sql:|pgx:|panic:|context\s|runtime error|Error: \w+\.\w+)/i.test(raw)
      if (looksTechnical && raw) console.warn('api: suppressed technical error', raw)
      // Special-case the email-verification gate: backend sends
      // {error: "email_not_verified", message: "..."} on 403. The SPA
      // matches on `code === "email_not_verified"` to render the banner
      // affordance rather than a generic toast.
      let code: string | undefined
      let message: string
      if (raw === 'email_not_verified') {
        code = 'email_not_verified'
        message = typeof body.message === 'string' && body.message
          ? body.message
          : terr('verify_email_gate')
      } else {
        message = looksTechnical ? fallback : raw
      }
      throw new ApiError(res.status, message, retryAfter, code)
    }

    return res.json()
  } finally {
    if (timer != null) window.clearTimeout(timer)
  }
}

// Auth
// AuthUser is the user shape returned by every auth endpoint. The slim
// `email_verified_at` field is what the banner reads; missing field is
// treated as "unverified" by useAuth so a stale localStorage user blob
// doesn't accidentally unblock the gate.
export interface AuthUser {
  id: string
  email: string
  name: string
  created_at?: string
  email_verified_at?: string | null
}

export async function login(email: string, password: string) {
  return request<{ token: string; user: AuthUser }>(
    '/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
  )
}

// confirmedMinimumAge is the UK ICO Children's Code self-attestation
// checkbox — backend rejects with 400 if false/missing. Required arg (not
// optional) so a caller can't silently forget the gate and ship a regression
// where every new account gets the audit timestamp.
export async function register(
  email: string,
  password: string,
  name: string,
  confirmedMinimumAge: boolean,
) {
  return request<{ token: string; user: AuthUser }>(
    '/auth/register',
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        name,
        confirmed_minimum_age: confirmedMinimumAge,
      }),
    },
  )
}

// Email verification — two endpoints. verifyEmail is public (the user
// might click the link from a different device that isn't signed in);
// resendVerification requires the JWT so the backend can identify which
// inbox to re-send to. Backend rate-limits resends at 3/hour per user.
export async function verifyEmail(token: string) {
  return request<{ message: string }>(
    '/auth/verify-email',
    { method: 'POST', body: JSON.stringify({ token }) },
  )
}

// The status field is the SPA-facing contract: 'sent' means a fresh
// verification email was queued, 'already_verified' means the server
// no-op'd because email_verified_at was already set. The banner branches
// on this to show the right toast and (for already_verified) trigger a
// /me refresh so the banner self-heals against stale localStorage state.
export async function resendVerification() {
  return request<{ status: 'sent' | 'already_verified'; message: string }>(
    '/auth/resend-verification',
    { method: 'POST', body: JSON.stringify({}) },
  )
}

// getMe re-reads the authenticated user's record. Called by useAuth on
// bootstrap so a stale localStorage `user` blob (e.g. one cached before
// the user clicked their verification link in a different browser)
// self-heals on the next page load. 401 fires the session-expired
// callback which logs the user out; other failures are tolerated and
// leave the cached user in place.
export async function getMe() {
  return request<AuthUser>('/me', { method: 'GET' })
}

// SupportedLocale enumerates the UI/content languages the server will
// accept. Mirrors the CHECK constraint in migrations/036 + /037 + /038
// and the supportedLocales map in internal/api/me_handler.go — keep
// all three in lockstep. en/it/de are shipped; es/fr/pt are
// pre-allowed at the type and DB layers so enabling them later is a
// bundle-drop, not a migration.
export type SupportedLocale = 'en' | 'it' | 'de' | 'es' | 'fr' | 'pt'

// UserPreferences mirrors the server-side store.UserPreferences shape.
// Each future preference field (e.g. email_notifications_enabled) lands
// here when the corresponding column is added in a migration.
export interface UserPreferences {
  user_id: string
  analytics_consent: 'accepted' | 'rejected' | null
  locale: SupportedLocale
  updated_at: string
}

// PreferencesPatch — partial update. Omitting a field means "do not
// change it" (the server's COALESCE arms preserve the existing value).
export interface PreferencesPatch {
  analytics_consent?: 'accepted' | 'rejected'
  locale?: SupportedLocale
}

export async function getPreferences() {
  return request<UserPreferences>('/me/preferences', { method: 'GET' })
}

export async function updatePreferences(patch: PreferencesPatch) {
  return request<UserPreferences>('/me/preferences', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

// Password reset — two-step flow. Request always returns 200 regardless of
// whether the email is registered (enumeration prevention); the caller
// shouldn't treat a successful response as confirmation that the email
// exists. Confirm returns 400 for any validation failure without telling
// the caller whether the token or the password was the problem.
export async function requestPasswordReset(email: string) {
  return request<{ message: string }>(
    '/auth/password-reset/request',
    { method: 'POST', body: JSON.stringify({ email }) },
  )
}

export async function confirmPasswordReset(token: string, newPassword: string) {
  // Returns the same {token, user} shape as login/register so the
  // frontend can auto-sign-in after a successful reset — the user
  // already proved email control, so a re-login step adds no security.
  return request<{ token: string; user: { id: string; email: string; name: string } }>(
    '/auth/password-reset/confirm',
    { method: 'POST', body: JSON.stringify({ token, new_password: newPassword }) },
  )
}

// Self-service profile + password change. updateProfile returns the updated
// user; changePassword returns a fresh {token, user} pair because bumping
// password_changed_at server-side invalidates the existing JWT — swapping
// the token keeps the session alive without forcing a re-login.
export async function updateProfile(name: string) {
  return request<{ id: string; email: string; name: string }>(
    '/me',
    { method: 'PATCH', body: JSON.stringify({ name }) },
  )
}

export async function changePassword(oldPassword: string, newPassword: string) {
  return request<{ token: string; user: { id: string; email: string; name: string } }>(
    '/me/password',
    {
      method: 'POST',
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    },
  )
}

// Email change is a two-step flow that mirrors password reset: requesting
// the change emails a confirmation link to the new address, and only when
// the link is clicked does users.email actually update. The user's existing
// JWT keeps working through the change — the JWT identifies users by ID,
// not email, so the session survives.
export async function requestEmailChange(newEmail: string, password: string) {
  return request<{ message: string }>(
    '/me/email/request',
    { method: 'POST', body: JSON.stringify({ new_email: newEmail, password }) },
  )
}

export async function confirmEmailChange(token: string) {
  return request<{ id: string; email: string; name: string; created_at: string }>(
    '/auth/email-change/confirm',
    { method: 'POST', body: JSON.stringify({ token }) },
  )
}

// Permanently delete the authenticated user's account and cascade-delete
// every owned row (courses, Q&A, notes, progress, etc.). Backend gates on
// the current password plus the literal confirmation string "DELETE" —
// either side rejects requests without both. Returns 204 on success; the
// caller should clear local storage and redirect to login immediately,
// since any subsequent request from this JWT will return 401.
export async function deleteAccount(password: string, confirmation: string) {
  return request<void>('/me', {
    method: 'DELETE',
    body: JSON.stringify({ password, confirmation }),
  })
}

// GDPR Article 20 data portability (#114). The backend returns a JSON
// dump of every row the user owns plus a Content-Disposition: attachment
// header; this helper drives the browser's native save-file flow off
// that header so the user gets a download dialog rather than a blob
// rendered inline. The filename comes from the server (embeds the
// user_id + UTC date) so multiple exports land as distinct files; the
// fallback is just for safety against a future header regression.
//
// Doesn't go through request() because that helper assumes a JSON
// response body it can .json()-parse; here the body is the file
// payload itself.
export async function exportMyData(): Promise<void> {
  const res = await fetch(`${BASE}/me/export`, {
    credentials: CREDS,
  })
  if (!res.ok) {
    // Surface the backend's error message when present so a 429 (rate
    // limited) or 401 (lost session) gets a meaningful toast; body parse
    // failure is non-fatal — fall back to a status-derived message.
    let msg = 'Failed to export data'
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) msg = body.error
    } catch {
      /* keep default */
    }
    throw new ApiError(res.status, msg)
  }
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = disposition.match(/filename="(.+)"/)
  const filename = match?.[1] ?? 'luno-learning-export.json'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export async function logout() {
  // Fire-and-forget: the backend revokes the JWT + clears the cookie.
  // Even on network failure the SPA proceeds to clear its in-memory
  // auth state so the user isn't stranded in a "click-logout-and-
  // nothing-happens" loop.
  await fetch(`${BASE}/auth/logout`, {
    method: 'POST',
    credentials: CREDS,
  })
}

// Courses
export async function listCourses() {
  return request<Course[]>('/courses')
}

export async function getCourse(courseId: string) {
  return request<Course>(`/courses/${courseId}`)
}

export async function deleteCourse(courseId: string) {
  return request<{ status: string }>(`/courses/${courseId}`, { method: 'DELETE' })
}

export async function updateCourse(courseId: string, input: { title: string; goals: string }) {
  return request<Course>(`/courses/${courseId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

// Learning dashboard
export async function getStats(days: number = 30) {
  return request<UserStats>(`/me/stats?days=${days}`)
}

// Personal notes
export async function listNotes(courseId: string, moduleId: string) {
  return request<ModuleNote[]>(`/courses/${courseId}/modules/${moduleId}/notes`)
}

// CreateNoteExtras carries the optional Memory-feed fields. Existing
// callers passing only `content` keep working unchanged; the toolbar Save
// flow passes `quote` + `anchor` (and optionally `comment` / `tags`) via
// the extras argument.
export interface CreateNoteExtras {
  quote?: string
  anchor?: Anchor
  comment?: string
  tags?: string[]
}

export async function createNote(
  courseId: string,
  moduleId: string,
  content: string,
  extras?: CreateNoteExtras,
) {
  const body: Record<string, unknown> = { content }
  if (extras?.quote !== undefined) body.quote = extras.quote
  if (extras?.anchor !== undefined) body.anchor = extras.anchor
  if (extras?.comment !== undefined) body.comment = extras.comment
  if (extras?.tags !== undefined) body.tags = extras.tags
  return request<ModuleNote>(`/courses/${courseId}/modules/${moduleId}/notes`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function updateNote(courseId: string, moduleId: string, noteId: string, content: string) {
  return request<ModuleNote>(`/courses/${courseId}/modules/${moduleId}/notes/${noteId}`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  })
}

// Edit a Highlight's comment (the user's note attached to a quoted
// passage). Distinct from updateNote, which targets the free-form Note
// card's `content`. Backend PATCH accepts both fields independently so
// each card edits only its own surface.
export async function updateNoteComment(courseId: string, moduleId: string, noteId: string, comment: string) {
  return request<ModuleNote>(`/courses/${courseId}/modules/${moduleId}/notes/${noteId}`, {
    method: 'PATCH',
    body: JSON.stringify({ comment }),
  })
}

export async function deleteNote(courseId: string, moduleId: string, noteId: string) {
  const res = await fetch(`${BASE}/courses/${courseId}/modules/${moduleId}/notes/${noteId}`, {
    method: 'DELETE',
    credentials: CREDS,
  })
  if (!res.ok) throw new ApiError(res.status, 'Failed to delete note')
}

// PreviewResponse is the unified two-phase preview shape introduced by
// #107. The backend wraps both the legacy synchronous outline and the
// durable async path in one envelope so the frontend doesn't branch on
// flag state:
//   - flag off (legacy):  preview_token === '', status === 'ready',
//                         outline populated.
//   - flag on (durable):  preview_token is a UUID, status starts as
//                         'generating', outline === null. Poll
//                         getCoursePreview(token) until status flips.
export interface PreviewResponse {
  preview_token: string
  status: CourseStatus
  outline: CourseOutline | null
}

export async function previewCourse(input: OnboardingInput, regenerateDirection?: string) {
  const body: OnboardingInput & { regenerate_direction?: string } = { ...input }
  if (regenerateDirection && regenerateDirection.trim()) {
    body.regenerate_direction = regenerateDirection.trim()
  }
  return request<PreviewResponse>('/courses/preview', {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

// getCoursePreview polls a durable preview job by token. Same envelope
// shape as previewCourse — status is 'generating' until the worker
// finishes, then flips to 'ready' (outline populated) or 'failed'.
export async function getCoursePreview(token: string) {
  // Backend route is /courses/preview/{token} (see internal/api/router.go).
  // No /me/ prefix — ownership is enforced inside the handler via a
  // user_id match on the job row, not by the URL shape.
  return request<PreviewResponse>(`/courses/preview/${token}`)
}

// Mirrors the server's course.ModuleSnapshot (generator_pivot.go) exactly.
// The /courses/preview/adjust-section handler strict-decodes the body
// (DisallowUnknownFields), so any extra key here — e.g. a module description —
// is rejected as a 400 before validation. Keep this to title/format/completed.
export interface ModuleSnapshot {
  title: string
  format: string
  completed: boolean
}

export async function previewAdjustSection(payload: {
  topic: string
  level: string
  section_title: string
  section_description: string
  current_modules: ModuleSnapshot[]
  direction: string
}) {
  return request<{ modules: { title: string; description: string; format: string; order: number; estimated_minutes: number }[] }>(
    '/courses/preview/adjust-section',
    {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: LLM_TIMEOUT_MS,
    },
  )
}

export interface TeaserResponse {
  facts: string[]
  locale: string
}

// getCourseTeaser fetches a few short "fun facts" about the topic to show
// while a course/outline generates. Fire-and-forget UX candy: callers should
// treat any rejection (or an empty `facts`) as "show no fact" and degrade —
// the backend itself returns 200 with an empty list when the feature is off.
// Short timeout: the teaser is meant to land in ~1-2s; if it's slow we'd
// rather drop it than have it hang behind the (much longer) outline wait.
export async function getCourseTeaser(topic: string, level?: string) {
  const params = new URLSearchParams({ topic })
  if (level) params.set('level', level)
  return request<TeaserResponse>(`/courses/teaser?${params.toString()}`, {
    timeoutMs: 15_000,
  })
}

export async function createCourse(input: OnboardingInput, outline?: CourseOutline) {
  return request<Course>('/courses', {
    method: 'POST',
    body: JSON.stringify({ ...input, outline }),
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

// retryCourseGeneration re-queues a failed durable-create job (#107).
// Backend returns 202 with no required body — we accept either an empty
// 202 (bypass request() which would try to parse JSON) or a tiny
// {status: 'generating'} blob. The caller should re-poll getCourse
// afterwards to see status flip back to 'generating' and then 'ready'.
export async function retryCourseGeneration(courseId: string): Promise<void> {
  const res = await fetch(`${BASE}/courses/${courseId}/retry`, {
    method: 'POST',
    credentials: CREDS,
  })
  if (!res.ok) {
    throw new ApiError(res.status, 'Failed to retry course generation')
  }
}

export async function listSections(courseId: string) {
  return request<Section[]>(`/courses/${courseId}/sections`)
}

// Modules
export async function getModule(courseId: string, moduleId: string) {
  // First-visit getModule generates content + checkpoint inline (the
  // backend's lookahead might not have run yet), which can stretch
  // toward the LLM budget. Subsequent fetches return cached content
  // and resolve in milliseconds.
  return request<Module>(`/courses/${courseId}/modules/${moduleId}`, {
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

export interface StreamModuleHandlers {
  // onDelta fires for each raw markdown chunk as the model writes it.
  onDelta: (chunk: string) => void
  // onDone fires once with the canonical post-processed module (authoritative
  // — the frontend swaps to rendering this). Also fires immediately, with no
  // preceding deltas, when content was already warm or generated by a
  // background pre-gen the request couldn't tap.
  onDone: (module: Module) => void
  // onError fires when the stream surfaces a generation error after it opened.
  onError?: (message: string) => void
  signal?: AbortSignal
}

// streamModule consumes the SSE lesson stream (GET …/modules/:id/stream).
// Uses fetch + ReadableStream rather than EventSource so the auth cookie is
// sent and we control the lifecycle. Cookie-based; mirrors request()'s
// credentials. Throws an ApiError if the stream can't be opened — the caller
// should fall back to getModule() (which also handles the 401-refresh dance).
export async function streamModule(
  courseId: string,
  moduleId: string,
  handlers: StreamModuleHandlers,
): Promise<void> {
  const res = await fetch(`${BASE}/courses/${courseId}/modules/${moduleId}/stream`, {
    credentials: CREDS,
    signal: handlers.signal,
    headers: { Accept: 'text/event-stream' },
  })
  if (!res.ok || !res.body) {
    throw new ApiError(res.status, 'Failed to start lesson stream')
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    // SSE events are separated by a blank line; an event may straddle chunk
    // boundaries, so we only consume complete (\n\n-terminated) blocks.
    let sep = buf.indexOf('\n\n')
    while (sep >= 0) {
      dispatchSSEEvent(buf.slice(0, sep), handlers)
      buf = buf.slice(sep + 2)
      sep = buf.indexOf('\n\n')
    }
  }
  if (buf.trim() !== '') dispatchSSEEvent(buf, handlers)
}

function dispatchSSEEvent(block: string, handlers: StreamModuleHandlers): void {
  let event = 'message'
  const dataLines: string[] = []
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''))
  }
  const data = dataLines.join('\n')
  if (data === '') return
  try {
    switch (event) {
      case 'delta':
        handlers.onDelta(JSON.parse(data) as string)
        break
      case 'done':
        handlers.onDone(JSON.parse(data) as Module)
        break
      case 'error':
        handlers.onError?.((JSON.parse(data) as { message?: string }).message ?? 'stream error')
        break
    }
  } catch {
    // Ignore a malformed/partial event payload rather than aborting the
    // whole stream over one bad frame.
  }
}

export async function completeModule(courseId: string, moduleId: string) {
  return request<{ status: string }>(
    `/courses/${courseId}/modules/${moduleId}/complete`,
    { method: 'POST' },
  )
}

export async function revertModule(courseId: string, moduleId: string) {
  // Revert re-runs the LLM for the prior version; same budget as content
  // generation.
  return request<Module>(`/courses/${courseId}/modules/${moduleId}/revert`, {
    method: 'POST',
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

export async function skipModule(courseId: string, moduleId: string) {
  return request<{ status: string }>(
    `/courses/${courseId}/modules/${moduleId}/skip`,
    { method: 'POST' },
  )
}

// regenerateModule is the user-facing escape hatch for stuck lessons:
// wipes the module's content + checkpoint server-side, then the caller
// re-fetches the module to trigger fresh lazy-gen. Bypasses the
// standard request() helper because the endpoint returns 204 (no body)
// and the helper's trailing res.json() would throw.
export async function regenerateModule(courseId: string, moduleId: string): Promise<void> {
  const res = await fetch(`${BASE}/courses/${courseId}/modules/${moduleId}/regenerate`, {
    method: 'POST',
    credentials: CREDS,
  })
  if (!res.ok) {
    throw new ApiError(res.status, 'Failed to start regeneration')
  }
}

// Delete a Q&A entry. Mirrors deleteNote — fail-closed 204 on success,
// throws on 404. Children of the deleted entry stay navigable; the
// thread-walk stops at the first missing parent.
export async function deleteQA(courseId: string, qaId: string): Promise<void> {
  const res = await fetch(`${BASE}/courses/${courseId}/qa/${qaId}`, {
    method: 'DELETE',
    credentials: CREDS,
  })
  if (!res.ok) throw new ApiError(res.status, 'Failed to delete Q&A entry')
}

// Phase 3.5 — grade an embedded `graded` exercise block. Returns the
// shape ExerciseBlock's GradedFeedback expects (score 0..1, summary,
// per-rubric-point pass/fail with optional comment). The prompt /
// model_answer / rubric_points come from the exercise block itself;
// answer is the learner's free-text submission.
export async function gradeEmbeddedExercise(
  courseId: string,
  moduleId: string,
  prompt: string,
  modelAnswer: string,
  rubricPoints: string[],
  answer: string,
): Promise<{
  score: number
  summary: string
  points: Array<{ point: string; met: boolean; comment?: string }>
}> {
  return request(
    `/courses/${courseId}/modules/${moduleId}/exercise/grade`,
    {
      method: 'POST',
      body: JSON.stringify({
        prompt,
        model_answer: modelAnswer,
        rubric_points: rubricPoints,
        answer,
      }),
      timeoutMs: LLM_TIMEOUT_MS,
    },
  )
}

// Study heartbeat — best-effort POST that returns 204 No Content. Bypasses
// the standard request() helper because that helper unconditionally calls
// res.json() (which would throw on 204), and because we want
// `keepalive: true` so a final flush right before page unload still gets
// sent. On 401 we trigger one refresh attempt and retry — without this,
// a token that expires mid-session silently stops all time-tracking until
// the user navigates somewhere that fires a non-heartbeat request.
export async function recordStudyHeartbeat(
  courseId: string,
  moduleId: string,
  deltaSeconds: number,
): Promise<void> {
  const path = `${BASE}/courses/${courseId}/modules/${moduleId}/study-heartbeat`
  const body = JSON.stringify({ delta_seconds: deltaSeconds })

  async function send(): Promise<Response> {
    return fetch(path, {
      method: 'POST',
      credentials: CREDS,
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    })
  }

  let res = await send()
  if (res.status === 401) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      res = await send()
    }
  }
  if (!res.ok) {
    throw new ApiError(res.status, 'heartbeat failed')
  }
}

// Pivot
export async function pivotCourse(
  courseId: string,
  reason: string,
  direction: string,
  scope: 'course' | 'section' | 'module' = 'course',
  sectionId?: string,
  moduleId?: string,
) {
  return request<Course>(`/courses/${courseId}/pivot`, {
    method: 'POST',
    body: JSON.stringify({
      reason,
      direction,
      scope,
      section_id: sectionId,
      module_id: moduleId,
    }),
    timeoutMs: PIVOT_TIMEOUT_MS,
  })
}

export interface PivotDiffModule {
  title: string
  section_title: string
}

// PivotPreviewResponse is the proposed result of a structural (course /
// section) pivot plus the kept/dropped diff. Exactly one of outline (course
// scope) / modules (section scope) is populated. Mirrors the backend
// pivotPreviewResponse in internal/api/pivot_preview_apply.go.
export interface PivotPreviewResponse {
  scope: 'course' | 'section'
  outline?: CourseOutline | null
  modules?: ModuleOutline[]
  kept: PivotDiffModule[]
  dropped: PivotDiffModule[]
}

// previewPivot generates the proposed outline/modules + diff WITHOUT mutating
// the course — the first half of the outline-as-destination change flow (#147).
export async function previewPivot(
  courseId: string,
  body: { reason: string; direction: string; scope: 'course' | 'section'; section_id?: string },
): Promise<PivotPreviewResponse> {
  return request<PivotPreviewResponse>(`/courses/${courseId}/pivot/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: PIVOT_TIMEOUT_MS,
  })
}

// applyPivot commits a previewed pivot, posting back the (possibly edited)
// proposed structure. Returns the updated course.
export async function applyPivot(
  courseId: string,
  body: { scope: 'course' | 'section'; reason: string; section_id?: string; outline?: CourseOutline | null; modules?: ModuleOutline[] },
): Promise<Course> {
  return request<Course>(`/courses/${courseId}/pivot/apply`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: PIVOT_TIMEOUT_MS,
  })
}

// Q&A
export async function listQA(courseId: string, moduleId?: string) {
  const params = moduleId ? `?module_id=${moduleId}` : ''
  return request<QAEntry[]>(`/courses/${courseId}/qa${params}`)
}

// AskQuestionExtras carries the optional anchor / threading metadata.
// Existing callers passing only `question` keep working unchanged. The
// Quote-to-Ask flow passes `quote` + `anchor`; "Ask AI" on a Highlight
// card adds `sourceNoteId`; "Ask AI" on a Q&A card adds `parentQAId` so
// the backend assembles multi-turn LLM context (D8).
export interface AskQuestionExtras {
  quote?: string
  anchor?: Anchor
  sourceNoteId?: string
  parentQAId?: string
}

export async function askQuestion(
  courseId: string,
  moduleId: string,
  question: string,
  extras?: AskQuestionExtras,
) {
  const body: Record<string, unknown> = { question }
  if (extras?.quote !== undefined) body.quote = extras.quote
  if (extras?.anchor !== undefined) body.anchor = extras.anchor
  if (extras?.sourceNoteId !== undefined) body.source_note_id = extras.sourceNoteId
  if (extras?.parentQAId !== undefined) body.parent_qa_id = extras.parentQAId
  return request<QAEntry>(`/courses/${courseId}/modules/${moduleId}/qa`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

// Selection-toolbar transforms. POST creates a Deepen / Simplify result
// and persists it; the Memory feed surfaces the row as a TransformCard
// (Tier 2 H2). See docs/memory-feed-design.md "Tier 2 — Deepen / Simplify".
export async function listTransforms(courseId: string, moduleId: string) {
  return request<ModuleTransform[]>(
    `/courses/${courseId}/modules/${moduleId}/transforms`,
  )
}

export async function deleteTransform(
  courseId: string,
  moduleId: string,
  transformId: string,
) {
  // No JSON body to parse on 204; bypass the standard request() helper
  // which unconditionally calls res.json(). Same pattern as deleteNote.
  const res = await fetch(
    `${BASE}/courses/${courseId}/modules/${moduleId}/transforms/${transformId}`,
    {
      method: 'DELETE',
      credentials: CREDS,
    },
  )
  if (!res.ok) throw new ApiError(res.status, 'Failed to delete transform')
}

export async function deepenPassage(
  courseId: string,
  moduleId: string,
  quote: string,
  anchor?: Anchor,
) {
  return transformPassage(courseId, moduleId, 'deepen', quote, anchor)
}

export async function simplifyPassage(
  courseId: string,
  moduleId: string,
  quote: string,
  anchor?: Anchor,
) {
  return transformPassage(courseId, moduleId, 'simplify', quote, anchor)
}

async function transformPassage(
  courseId: string,
  moduleId: string,
  kind: 'deepen' | 'simplify',
  quote: string,
  anchor?: Anchor,
) {
  const body: Record<string, unknown> = { quote }
  if (anchor) body.anchor = anchor
  return request<ModuleTransform>(`/courses/${courseId}/modules/${moduleId}/${kind}`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

// Checkpoints
export async function getCheckpoint(courseId: string, moduleId: string) {
  // First-visit getCheckpoint can run inline LLM generation if the
  // lookahead hasn't landed one yet — same budget as getModule.
  return request<Checkpoint>(`/courses/${courseId}/modules/${moduleId}/checkpoint`, {
    timeoutMs: LLM_TIMEOUT_MS,
  })
}

export async function submitCheckpoint(courseId: string, moduleId: string, answers: number[]) {
  return request<Checkpoint>(`/courses/${courseId}/modules/${moduleId}/checkpoint`, {
    method: 'POST',
    body: JSON.stringify({ answers }),
  })
}

// PollOptions tune the backoff curve of pollUntil. intervalMs is the
// starting delay between attempts; the helper doubles it each tick up
// to maxIntervalMs. signal aborts the in-flight wait (and rejects the
// returned promise with AbortError) — used by component unmounts.
export interface PollOptions {
  intervalMs?: number
  maxIntervalMs?: number
  signal?: AbortSignal
}

// MAX_TRANSIENT_POLL_FAILURES is how many CONSECUTIVE transient fetch
// rejections pollUntil rides out before giving up. One blip mid-poll
// used to surface "create failed" while the course was actually created
// (it then appeared on the dashboard) and park GeneratingState on an
// error until a manual refresh (audit B16). A short tolerance absorbs a
// single dropped request / DNS hiccup / gateway restart without masking
// a genuinely stuck backend — three straight failures is still a fast
// surface. The counter resets on any success, so an intermittent flap
// never accumulates to the cap.
const MAX_TRANSIENT_POLL_FAILURES = 3

// pollUntil repeatedly calls `fetch` until `ready(result)` returns true,
// resolving with that result. Used by the durable course/preview flow
// (#107) for both course-readiness polling (every 2s, capped at 10s)
// and preview-readiness polling. AbortSignal-aware so an unmounting
// component can stop the loop mid-wait.
//
// Backoff curve: 2s, 4s, 8s, 10s, 10s, … This pays for the realistic
// case (generation finishes in 5-30s) without hammering the API for a
// pathological 2-minute outlier.
//
// Transient fetch rejections (network / timeout — surfaced as
// `ApiError` with `status === 0`, or a bare Error) are tolerated up to
// MAX_TRANSIENT_POLL_FAILURES consecutive times before surfacing; any
// success resets the counter. A non-transient `ApiError` (a real HTTP
// status like 401/404) is a definitive answer from the server, not a
// blip, so it rethrows immediately. AbortError is never swallowed.
export async function pollUntil<T>(
  fetch: () => Promise<T>,
  ready: (t: T) => boolean,
  opts: PollOptions = {},
): Promise<T> {
  const baseInterval = opts.intervalMs ?? 2_000
  const maxInterval = opts.maxIntervalMs ?? 10_000
  const signal = opts.signal

  let delay = baseInterval
  let consecutiveFailures = 0
  // First fetch fires immediately — the work is often already done by
  // the time the caller starts polling (legacy flag-off path or a fast
  // generation).
  for (;;) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    try {
      const result = await fetch()
      consecutiveFailures = 0
      if (ready(result)) return result
    } catch (err) {
      if (!isTransientPollError(err)) throw err
      consecutiveFailures += 1
      if (consecutiveFailures >= MAX_TRANSIENT_POLL_FAILURES) throw err
    }
    await sleep(delay, signal)
    delay = Math.min(delay * 2, maxInterval)
  }
}

// isTransientPollError decides whether a poll fetcher rejection is worth
// riding out. Network/timeout failures (an `ApiError` with `status === 0`
// or any non-ApiError rejection) are transient. An abort must always
// propagate so an unmounting component stops the loop. An `ApiError`
// with a real HTTP status (4xx/5xx) is a definitive server answer, not
// a blip — surface it immediately.
function isTransientPollError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false
  if (err instanceof ApiError) return err.status === 0
  return true
}

// sleep is AbortSignal-aware: aborting the signal rejects the returned
// promise with an AbortError so pollUntil can bail out mid-wait without
// the next fetch landing.
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}
